import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ReadableNote } from '../components/ReadableNote';
import { weightChartEntries, weightDisplay } from '../lib/weightDisplay';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Scale, TrendingUp, TrendingDown, Minus, Plus, Calendar } from 'lucide-react';

const AthleteWeightPage = () => {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [teamId, setTeamId] = useState(null);
  
  const [newEntry, setNewEntry] = useState({
    weight_kg: '',
    note: ''
  });

  const athleteId = user?.athlete_id;

  // Fetch athlete's team for creating entries
  const fetchAthleteTeam = useCallback(async () => {
    if (!athleteId) return;
    try {
      const res = await api.get(`/athletes/${athleteId}/teams?include_history=false`);
      const primaryTeam = res.data.find(t => t.assignment_type === 'PRIMARY');
      if (primaryTeam) {
        setTeamId(primaryTeam.team_id);
      }
    } catch (err) {
      console.error('Failed to fetch athlete team:', err);
    }
  }, [api, athleteId]);

  const fetchWeightData = useCallback(async () => {
    if (!athleteId) return;
    
    try {
      setLoading(true);
      setLoadError(false);
      const [summaryRes, entriesRes] = await Promise.all([
        api.get(`/weight/athlete/${athleteId}/summary`),
        api.get(`/weight/athlete/${athleteId}?limit=50`)
      ]);
      setSummary(summaryRes.data);
      setEntries(entriesRes.data);
    } catch (err) {
      setLoadError(true);
      console.error('Failed to fetch weight data:', err);
    } finally {
      setLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => {
    fetchAthleteTeam();
    fetchWeightData();
  }, [fetchAthleteTeam, fetchWeightData]);

  const handleAddEntry = async () => {
    if (!newEntry.weight_kg) {
      toast.error(language === 'IT' ? 'Inserisci il peso' : 'Please enter your weight');
      return;
    }
    
    try {
      await api.post('/weight', {
        athlete_id: athleteId,
        team_id: teamId,
        date: new Date().toISOString().split('T')[0],
        weight_kg: parseFloat(newEntry.weight_kg),
        note: newEntry.note || null
      });
      toast.success(language === 'IT' ? 'Peso registrato' : 'Weight recorded');
      setShowAddDialog(false);
      setNewEntry({ weight_kg: '', note: '' });
      fetchWeightData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || (language === 'IT' ? 'Errore nel salvataggio' : 'Failed to save weight');
      toast.error(errorMsg);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'UP') return <TrendingUp className="w-5 h-5 text-amber-400" />;
    if (trend === 'DOWN') return <TrendingDown className="w-5 h-5 text-blue-400" />;
    return <Minus className="w-5 h-5 text-zinc-400" />;
  };

  const getTrendColor = (change) => {
    if (!change) return 'text-zinc-400';
    if (change > 0) return 'text-amber-400';
    if (change < 0) return 'text-blue-400';
    return 'text-zinc-400';
  };

  // Simple SVG line chart for weight trend
  const renderChart = () => {
    if (!entries || entries.length < 2) return null;
    
    const chartEntries = weightChartEntries(entries);
    if (chartEntries.length < 2) return null;
    if (chartEntries.length < 2) return null;
    
    const weights = chartEntries.map(e => e.weight_kg);
    const minWeight = Math.min(...weights) - 1;
    const maxWeight = Math.max(...weights) + 1;
    const range = maxWeight - minWeight || 1;
    
    const width = 400;
    const height = 150;
    const padding = 15;
    
    const points = chartEntries.map((entry, i) => {
      const x = padding + (i / (chartEntries.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((entry.weight_kg - minWeight) / range) * (height - 2 * padding);
      return { x, y, weight: entry.weight_kg, date: entry.date };
    });
    
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    
    return (
      <div className="mt-6" data-testid="weight-chart">
        <h3 className="text-sm font-medium text-zinc-400 mb-3">
          {language === 'IT' ? 'Andamento Peso' : 'Weight Trend'}
        </h3>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          {/* Grid lines */}
          <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#3f3f46" strokeWidth="1" />
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#3f3f46" strokeWidth="1" />
          
          {/* Horizontal grid lines */}
          {[0.25, 0.5, 0.75].map((ratio, i) => (
            <line key={i} x1={padding} y1={padding + ratio * (height - 2 * padding)} x2={width - padding} y2={padding + ratio * (height - 2 * padding)} stroke="#27272a" strokeWidth="1" strokeDasharray="4 4" />
          ))}
          
          {/* Y-axis labels */}
          <text x="5" y={padding + 5} fill="#71717a" fontSize="10">{maxWeight.toFixed(0)}</text>
          <text x="5" y={height - padding} fill="#71717a" fontSize="10">{minWeight.toFixed(0)}</text>
          
          {/* Trend line */}
          <path d={pathD} fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          
          {/* Data points */}
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="4" fill="#f59e0b" stroke="#18181b" strokeWidth="2" />
          ))}
        </svg>
        <div className="flex justify-between text-xs text-zinc-500 mt-1 px-4">
          <span>{chartEntries[0]?.date}</span>
          <span>{chartEntries[chartEntries.length - 1]?.date}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {language === 'IT' ? 'Il Mio Peso' : 'My Weight'}
          </h1>
        </div>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="text-zinc-500 text-center">{language === 'IT' ? 'Caricamento...' : 'Loading...'}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const todayEntry = entries.find(e => e.date === new Date().toISOString().split('T')[0]);

  return (
    <div className="space-y-6 fade-in" data-testid="athlete-weight-page">
      {loadError && <p role="alert" className="text-amber-300" data-testid="athlete-weight-load-error">{language === 'IT' ? 'Impossibile caricare le misurazioni del peso' : 'Unable to load weight measurements'}</p>}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {language === 'IT' ? 'Il Mio Peso' : 'My Weight'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {language === 'IT' ? 'Registra il tuo peso giornaliero' : 'Track your daily weight'}
          </p>
        </div>
        {!todayEntry && (
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
            data-testid="add-weight-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {language === 'IT' ? 'Registra Peso' : 'Log Weight'}
          </Button>
        )}
      </div>

      {/* Quick Add Card - shown if today's weight not logged */}
      {!todayEntry && summary?.latest && (
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/30" data-testid="quick-add-prompt">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Scale className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-zinc-100 font-medium">
                    {language === 'IT' ? "Non hai ancora registrato il peso di oggi" : "You haven't logged today's weight yet"}
                  </p>
                  <p className="text-sm text-zinc-400">
                    {language === 'IT' ? 'Ultimo peso registrato: ' : 'Last recorded: '}{weightDisplay(summary.latest.weight_kg, language)} ({summary.latest.date})
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Registra' : 'Log Now'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's weight logged confirmation */}
      {todayEntry && (
        <Card className="bg-emerald-500/10 border-emerald-500/30" data-testid="today-logged">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <Scale className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-emerald-400 font-medium">
                  {language === 'IT' ? 'Peso di oggi registrato' : "Today's weight logged"}: <span className="font-bold">{weightDisplay(todayEntry.weight_kg, language)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Stats */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Scale className="w-5 h-5 text-amber-500" />
            {language === 'IT' ? 'Riepilogo' : 'Summary'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {summary?.latest ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-4 text-center">
                <div className="text-xl font-bold text-amber-400 break-words" data-testid="current-weight">{weightDisplay(summary.latest.weight_kg, language)}</div>
                <div className="text-xs text-zinc-500 mt-1">{language === 'IT' ? 'Attuale' : 'Current'}</div>
              </div>
              <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  {getTrendIcon(summary.trend)}
                  <span className={`text-2xl font-bold ${getTrendColor(summary.change_kg)}`} data-testid="weight-diff">
                    {summary.change_kg > 0 ? '+' : ''}{summary.change_kg ?? '—'}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">{language === 'IT' ? 'kg variazione' : 'kg change'}</div>
              </div>
              <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-4 text-center">
                <div className="text-2xl font-medium text-zinc-300" data-testid="avg-7day">
                  {summary.seven_day_avg ?? '—'}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{language === 'IT' ? 'media 7 giorni' : '7-day avg'}</div>
              </div>
              <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-4 text-center">
                <div className="text-lg font-medium text-zinc-300" data-testid="weight-entry-count">{summary.total_entries}</div>
                <div className="text-xs text-zinc-500 mt-1">{language === 'IT' ? 'registrazioni' : 'entries'}</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8" data-testid="no-weight-data">
              <Scale className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
              <p className="text-zinc-400 text-lg">
                {language === 'IT' ? 'Peso non disponibile' : 'Weight unavailable'}
              </p>
              <p className="text-zinc-500 text-sm mt-2">
                {language === 'IT' ? 'Inizia a tracciare il tuo peso oggi!' : 'Start tracking your weight today!'}
              </p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400 mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Prima Registrazione' : 'First Entry'}
              </Button>
            </div>
          )}

          {/* Weight Chart */}
          {renderChart()}
        </CardContent>
      </Card>

      {/* Weight History */}
      {entries.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-zinc-500" />
              {language === 'IT' ? 'Storico' : 'History'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-zinc-800">
              {entries.map((entry) => (
                <div key={entry.id} className="flex flex-wrap items-center justify-between gap-2 py-3 min-w-0" data-testid={`weight-entry-${entry.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="text-amber-400 font-mono font-bold text-lg" data-testid={`athlete-weight-value-${entry.id}`}>{weightDisplay(entry.weight_kg, language)}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-zinc-400 text-sm">{entry.date}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      entry.source === 'ATHLETE' ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700/50 text-zinc-400'
                    }`}>
                      {entry.source === 'ATHLETE' ? (language === 'IT' ? 'Tu' : 'You') : 'Staff'}
                    </span>
                  </div>
                  <ReadableNote testId={`athlete-weight-note-${entry.id}`}>{entry.note}</ReadableNote>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Weight Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-500" />
              {language === 'IT' ? 'Registra Peso' : 'Log Weight'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">{language === 'IT' ? 'Peso (kg)' : 'Weight (kg)'}</Label>
              <Input
                type="number"
                step="0.1"
                min="30"
                max="200"
                value={newEntry.weight_kg}
                onChange={(e) => setNewEntry({ ...newEntry, weight_kg: e.target.value })}
                placeholder={summary?.latest ? `${language === 'IT' ? 'Ultimo' : 'Last'}: ${summary.latest.weight_kg}` : 'e.g., 75.5'}
                className="bg-zinc-950 border-zinc-800 text-2xl font-mono text-center h-16"
                autoFocus
                data-testid="weight-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">{language === 'IT' ? 'Nota (opzionale)' : 'Note (optional)'}</Label>
              <Textarea
                value={newEntry.note}
                onChange={(e) => setNewEntry({ ...newEntry, note: e.target.value })}
                placeholder={language === 'IT' ? 'es: Prima di colazione' : 'e.g., Before breakfast'}
                className="bg-zinc-950 border-zinc-800"
                rows={2}
                data-testid="weight-note"
              />
            </div>
            <div className="text-xs text-zinc-500 text-center">
              {language === 'IT' ? 'Data: ' : 'Date: '}{new Date().toISOString().split('T')[0]}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="border-zinc-700 text-zinc-300"
            >
              {language === 'IT' ? 'Annulla' : 'Cancel'}
            </Button>
            <Button
              onClick={handleAddEntry}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
              data-testid="save-weight-btn"
            >
              {language === 'IT' ? 'Salva' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AthleteWeightPage;
